import { Tooltip } from '@zendeskgarden/react-tooltips'
import moment from 'moment'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

import { Item } from '../../models/Item'
import { AvailabilityByDate, AvailabilityFilter } from '../../utils/calendar'
import { DATE_KEY_FORMAT, isDayInMonth, isToday } from '../../utils/dates'
import Button from '../app/design/Button'
import Label from '../app/design/Label'

type Props = {
    day: moment.Moment
    selectedDay: moment.Moment | null
    selectedMonth: moment.Moment
    selectedItem: Item | null | undefined
    availabiltyByDate: AvailabilityByDate
    availabilityFilter: AvailabilityFilter
    onSelect: (day: moment.Moment) => void
}

export const CalendarAvailabilityDateButton = ({
    day,
    selectedDay,
    selectedMonth,
    selectedItem,
    availabiltyByDate,
    availabilityFilter,
    onSelect,
}: Props) => {
    const MAX_DURATION_IN_WEEKS = 10
    const duration: number = useMemo(
        () => availabilityFilter.duration.value,
        [availabilityFilter.duration.value]
    )
    const isOpenEndedReservation = useMemo(
        () => selectedItem?.type === 'reservation' && !duration,
        [selectedItem, duration]
    )
    const isSelectedDay = useMemo(() => day.isSame(selectedDay, 'day'), [day, selectedDay])
    const selectedEndDay = useMemo(
        () =>
            selectedDay && duration >= 1440 ? selectedDay.clone().add(duration, 'minutes') : null,
        [selectedDay, duration]
    )
    const isSelectedEndDay = useMemo(
        () => selectedEndDay && day.isSame(selectedEndDay, 'day'),
        [selectedEndDay, day]
    )
    const isDayInSelectedMonth = useMemo(
        () => isDayInMonth(day, selectedMonth.month()),
        [day, selectedMonth]
    )
    const isDayInSelectedRange = useMemo(
        () => selectedEndDay && day.isBetween(selectedDay, selectedEndDay, 'day', '()'),
        [selectedEndDay, day, selectedDay]
    )

    const count = useMemo(
        () =>
            (availabiltyByDate[day.format(DATE_KEY_FORMAT)] ?? []).filter((a) => a.available)
                .length,
        [availabiltyByDate, day]
    )

    const dayClassName = useMemo(() => {
        if (isDayInSelectedRange)
            return 'bg-info-200 hover:bg-info-100 first:rounded-l-lg last:rounded-r-lg'
        if (isSelectedDay && !!selectedEndDay)
            return 'after:content-[""] after:absolute after:top-0 after:right-0 after:h-8 after:w-[22px] after:bg-info-200 last:after:rounded-r-lg'
        if (isSelectedEndDay)
            return 'before:content-[""] before:absolute before:top-0 before:left-0 before:h-8 before:w-[22px] before:bg-info-200 first:before:rounded-l-lg'
        return 'day'
    }, [
        isDayInSelectedRange,
        isDayInSelectedMonth,
        isSelectedDay,
        selectedEndDay,
        isSelectedEndDay,
    ])

    const exceedsMaxDuration = useMemo(() => {
        if (!selectedDay || !isOpenEndedReservation) return false
        return Math.abs(day.diff(selectedDay, 'minutes')) > MAX_DURATION_IN_WEEKS * 7 * 24 * 60
    }, [selectedDay, isOpenEndedReservation, day])

    return (
        <div className={twMerge('relative mx-0 my-[2px] w-[45px] text-center', dayClassName)}>
            {!isSelectedDay && !isSelectedEndDay && (
                <>
                    {!exceedsMaxDuration && (
                        <Button
                            shape="circle"
                            size="small"
                            intent="tertiary_deprecated"
                            className={`relative no-underline ${isToday(day) ? 'text-info-400' : count === 0 ? 'text-gray-300' : ''}`}
                            style={{ visibility: isDayInSelectedMonth ? 'visible' : 'hidden' }}
                            onClick={() => onSelect(day)}
                            // TODO: potentially disable for class item types w/ fixed start dates
                        >
                            <Label className="text-gray-700">{day.date()}</Label>
                        </Button>
                    )}

                    {exceedsMaxDuration && (
                        <Tooltip
                            content={`${MAX_DURATION_IN_WEEKS} week maximum`}
                            style={{
                                background: '#3C94A4',
                                borderRadius: 8,
                                padding: 12,
                                fontSize: 13,
                            }}
                        >
                            <Button
                                shape="circle"
                                size="small"
                                intent="tertiary_deprecated"
                                className={`relative cursor-none line-through ${isToday(day) ? 'text-info-200' : ''}`}
                                style={{ visibility: isDayInSelectedMonth ? 'visible' : 'hidden' }}
                                disabled
                            >
                                <Label className="text-gray-400">{day.date()}</Label>
                            </Button>
                        </Tooltip>
                    )}

                    {isDayInSelectedMonth && count > 0 && (
                        <div className="pointer-events-none absolute bottom-0 w-[45px] text-center text-[9px]/3 font-medium text-gray-600">
                            {count} slot{count === 1 ? '' : 's'}
                        </div>
                    )}
                </>
            )}
            {isSelectedDay && !selectedEndDay && (
                <Button
                    shape="circle"
                    size="small"
                    intent="primary"
                    className="relative"
                    style={{ visibility: isDayInSelectedMonth ? 'visible' : 'hidden' }}
                >
                    <Label>{day.date()}</Label>
                </Button>
            )}
            {isSelectedDay && !!selectedEndDay && (
                <Tooltip
                    content="Check-in day"
                    style={{
                        background: '#3C94A4',
                        borderRadius: 8,
                        padding: 12,
                        fontSize: 13,
                    }}
                >
                    <Button
                        shape="circle"
                        size="small"
                        intent="primary"
                        className="relative"
                        style={{ visibility: isDayInSelectedMonth ? 'visible' : 'hidden' }}
                    >
                        <Label>{day.date()}</Label>
                    </Button>
                </Tooltip>
            )}
            {isSelectedEndDay && (
                <Tooltip
                    content="Check-out day"
                    style={{
                        background: '#3C94A4',
                        borderRadius: 8,
                        padding: 12,
                        fontSize: 13,
                    }}
                >
                    <Button
                        shape="circle"
                        size="small"
                        intent="primary"
                        className="relative"
                        style={{ visibility: isDayInSelectedMonth ? 'visible' : 'hidden' }}
                        onClick={() => onSelect(day)}
                    >
                        <Label>{day.clone().add(duration).date()}</Label>
                    </Button>
                </Tooltip>
            )}
        </div>
    )
}
